import { createContext, useCallback, useContext, useMemo, useReducer } from 'react'
import * as productService from '../services/productService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'
import {
  ProductActionType,
  createInitialProductState,
  productReducer,
} from './reducers/productReducer'

const ProductContext = createContext<any>(null)

export function ProductProvider({ children }) {
  const [state, dispatch] = useReducer(productReducer, undefined, createInitialProductState)

  const fetchProducts = useCallback(
    async (
      params: productService.ProductsListParams = {},
      opts: { totalsOnly?: boolean } = {},
    ) => {
      if (!opts.totalsOnly) dispatch({ type: ProductActionType.LOAD_LIST_START })
      try {
        const data = await productService.fetchProducts(params)
        if (opts.totalsOnly) {
          dispatch({ type: ProductActionType.SET_LIST_TOTAL, payload: data.total })
        } else {
          dispatch({ type: ProductActionType.LOAD_LIST_SUCCESS, payload: data })
        }
        return data
      } catch (e) {
        dispatch({
          type: ProductActionType.LOAD_LIST_FAILURE,
          payload: getUserFacingErrorMessage(e, 'Failed to load products'),
        })
        throw e
      }
    },
    [],
  )

  const getProductById = useCallback(async (id) => {
    try {
      const item = await productService.fetchProductById(id)
      dispatch({ type: ProductActionType.SET_SELECTED, payload: item })
      return item
    } catch (e) {
      dispatch({
        type: ProductActionType.SET_ERROR,
        payload: getUserFacingErrorMessage(e, 'Failed to load product'),
      })
      throw e
    }
  }, [])

  const createProduct = useCallback(async (payload) => {
    dispatch({ type: ProductActionType.MUTATION_START })
    try {
      const created = await productService.createProduct(payload)
      dispatch({ type: ProductActionType.CREATE_SUCCESS, payload: created })
      return created
    } catch (e) {
      dispatch({
        type: ProductActionType.SET_ERROR,
        payload: getUserFacingErrorMessage(e, 'Failed to create product'),
      })
      throw e
    }
  }, [])

  const updateProduct = useCallback(async (id, payload) => {
    dispatch({ type: ProductActionType.MUTATION_START })
    try {
      const updated = await productService.updateProduct(id, payload)
      dispatch({ type: ProductActionType.UPDATE_SUCCESS, payload: updated })
      return updated
    } catch (e) {
      dispatch({
        type: ProductActionType.SET_ERROR,
        payload: getUserFacingErrorMessage(e, 'Failed to update product'),
      })
      throw e
    }
  }, [])

  const deleteProduct = useCallback(async (id) => {
    dispatch({ type: ProductActionType.MUTATION_START })
    try {
      await productService.deleteProduct(id)
      dispatch({ type: ProductActionType.DELETE_SUCCESS, payload: id })
    } catch (e) {
      dispatch({
        type: ProductActionType.SET_ERROR,
        payload: getUserFacingErrorMessage(e, 'Failed to delete product'),
      })
      throw e
    }
  }, [])

  const clearError = useCallback(() => {
    dispatch({ type: ProductActionType.CLEAR_ERROR })
  }, [])

  const value = useMemo(
    () => ({
      products: state.products,
      listTotal: state.listTotal,
      listPage: state.listPage,
      listLimit: state.listLimit,
      selectedProduct: state.selectedProduct,
      listLoading: state.listLoading,
      mutationLoading: state.mutationLoading,
      error: state.error,
      fetchProducts,
      getProductById,
      createProduct,
      updateProduct,
      deleteProduct,
      clearError,
    }),
    [state, fetchProducts, getProductById, createProduct, updateProduct, deleteProduct, clearError],
  )

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
}

export function useProduct() {
  const ctx = useContext(ProductContext)
  if (!ctx) throw new Error('useProduct must be used within ProductProvider')
  return ctx
}
